/**
 * AnalyticsSummary - Stories 24.53-24.56
 * Campaign and content analytics visualization
 */

import React from 'react';
import {
  TrendingUp, TrendingDown, Minus, BarChart3, PieChart,
  Eye, Users, MousePointerClick, Clock, Target,
  ArrowUpRight, ArrowDownRight, Sparkles, Calendar
} from 'lucide-react';

export interface MetricData {
  id: string;
  label: string;
  value: number | string;
  previousValue?: number | string;
  change?: number; // percentage
  trend?: 'up' | 'down' | 'neutral';
  format?: 'number' | 'percentage' | 'currency' | 'time';
  icon?: React.ElementType;
  color?: string;
}

export interface ChartData {
  label: string;
  value: number;
  color?: string;
}

interface AnalyticsSummaryProps {
  title: string;
  period?: string;
  metrics: MetricData[];
  chartData?: ChartData[];
  chartType?: 'bar' | 'donut' | 'sparkline';
  insights?: string[];
  onPeriodChange?: (period: string) => void;
  isLoading?: boolean;
  className?: string;
}

export const AnalyticsSummary: React.FC<AnalyticsSummaryProps> = ({
  title,
  period = 'Last 30 days',
  metrics,
  chartData,
  chartType = 'bar',
  insights,
  onPeriodChange,
  isLoading = false,
  className = ''
}) => {
  const formatValue = (value: number | string, format?: string): string => {
    if (typeof value === 'string') return value;
    
    switch (format) {
      case 'percentage': return `${value.toFixed(1)}%`;
      case 'currency': return `$${value.toLocaleString()}`;
      case 'time': return `${Math.floor(value / 60)}m ${value % 60}s`;
      default: return value.toLocaleString();
    }
  };

  const getTrendIcon = (trend?: string) => {
    switch (trend) {
      case 'up': return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'down': return <TrendingDown className="w-4 h-4 text-red-500" />;
      default: return <Minus className="w-4 h-4 text-gray-400" />;
    }
  };

  const getTrendColor = (trend?: string) => {
    switch (trend) {
      case 'up': return 'text-green-600 bg-green-100';
      case 'down': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  // Simple bar chart
  const renderBarChart = () => {
    if (!chartData) return null;
    const maxValue = Math.max(...chartData.map(d => d.value));

    return (
      <div className="space-y-2">
        {chartData.map((item, idx) => (
          <div key={idx} className="flex items-center gap-3">
            <span className="text-sm text-gray-600 w-24 truncate">{item.label}</span>
            <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${item.color || 'bg-blue-500'}`}
                style={{ width: `${(item.value / maxValue) * 100}%` }}
              />
            </div>
            <span className="text-sm font-medium text-gray-800 w-16 text-right">
              {item.value.toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    );
  };

  // Simple donut chart (CSS-based)
  const renderDonutChart = () => {
    if (!chartData) return null;
    const total = chartData.reduce((sum, d) => sum + d.value, 0);
    let accumulated = 0;

    const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

    return (
      <div className="flex items-center gap-6">
        <div
          className="w-32 h-32 rounded-full relative"
          style={{
            background: `conic-gradient(${
              chartData.map((d, i) => {
                const start = (accumulated / total) * 360;
                accumulated += d.value;
                const end = (accumulated / total) * 360;
                return `${d.color || colors[i % colors.length]} ${start}deg ${end}deg`;
              }).join(', ')
            })`
          }}
        >
          <div className="absolute inset-4 bg-white rounded-full flex items-center justify-center">
            <span className="text-xl font-bold text-gray-800">{total.toLocaleString()}</span>
          </div>
        </div>
        <div className="space-y-2">
          {chartData.map((item, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: item.color || colors[idx % colors.length] }}
              />
              <span className="text-sm text-gray-600">{item.label}</span>
              <span className="text-sm font-medium text-gray-800">
                {((item.value / total) * 100).toFixed(1)}%
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Sparkline chart
  const renderSparkline = () => {
    if (!chartData) return null;
    const maxValue = Math.max(...chartData.map(d => d.value));
    const minValue = Math.min(...chartData.map(d => d.value));
    const range = maxValue - minValue || 1;

    const points = chartData.map((d, i) => {
      const x = (i / (chartData.length - 1)) * 100;
      const y = 100 - ((d.value - minValue) / range) * 100;
      return `${x},${y}`;
    }).join(' ');

    return (
      <svg viewBox="0 0 100 50" className="w-full h-16">
        <polyline
          points={points}
          fill="none"
          stroke="#3B82F6"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
        </linearGradient>
        <polygon
          points={`0,50 ${points} 100,50`}
          fill="url(#gradient)"
        />
      </svg>
    );
  };

  if (isLoading) {
    return (
      <div className={`bg-white rounded-xl border border-gray-200 p-6 ${className}`}>
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-24 bg-gray-100 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl border border-gray-200 ${className}`}>
      {/* Header */}
      <div className="p-6 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-800">{title}</h3>
            <p className="text-sm text-gray-500 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {period}
            </p>
          </div>
        </div>
        {onPeriodChange && (
          <select
            value={period}
            onChange={(e) => onPeriodChange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="Last 7 days">Last 7 days</option>
            <option value="Last 30 days">Last 30 days</option>
            <option value="Last 90 days">Last 90 days</option>
            <option value="This year">This year</option>
          </select>
        )}
      </div>

      {/* Metrics grid */}
      <div className="p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        {metrics.map((metric) => {
          const Icon = metric.icon || Target;
          return (
            <div
              key={metric.id}
              className="p-4 bg-gray-50 rounded-lg"
            >
              <div className="flex items-center justify-between mb-2">
                <Icon className={`w-5 h-5 ${metric.color || 'text-gray-500'}`} />
                {metric.change !== undefined && (
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex items-center gap-1 ${getTrendColor(metric.trend)}`}>
                    {metric.trend === 'up' ? (
                      <ArrowUpRight className="w-3 h-3" />
                    ) : metric.trend === 'down' ? (
                      <ArrowDownRight className="w-3 h-3" />
                    ) : null}
                    {Math.abs(metric.change)}%
                  </span>
                )}
              </div>
              <div className="text-2xl font-bold text-gray-800">
                {formatValue(metric.value, metric.format)}
              </div>
              <div className="text-sm text-gray-600">{metric.label}</div>
            </div>
          );
        })}
      </div>

      {/* Chart */}
      {chartData && (
        <div className="px-6 pb-6">
          {chartType === 'bar' && renderBarChart()}
          {chartType === 'donut' && renderDonutChart()}
          {chartType === 'sparkline' && renderSparkline()}
        </div>
      )}

      {/* AI Insights */}
      {insights && insights.length > 0 && (
        <div className="p-6 border-t border-gray-200 bg-gradient-to-r from-purple-50 to-blue-50">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-purple-600" />
            <span className="font-medium text-purple-800">AI Insights</span>
          </div>
          <ul className="space-y-2">
            {insights.map((insight, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                <span className="text-purple-500">•</span>
                {insight}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default AnalyticsSummary;
